import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const BudgetCalculator = ({ totalBudget = 75000 }) => {
  const [budget, setBudget] = useState(totalBudget);
  const [categories, setCategories] = useState([
    { id: 1, name: 'Venue', percentage: 30, amount: 22500, color: 'bg-blue-500' },
    { id: 2, name: 'Catering', percentage: 25, amount: 18750, color: 'bg-green-500' },
    { id: 3, name: 'Photography', percentage: 15, amount: 11250, color: 'bg-purple-500' },
    { id: 4, name: 'Decorations', percentage: 10, amount: 7500, color: 'bg-yellow-500' },
    { id: 5, name: 'Attire & Beauty', percentage: 8, amount: 6000, color: 'bg-pink-500' },
    { id: 6, name: 'Entertainment', percentage: 5, amount: 3750, color: 'bg-indigo-500' },
    { id: 7, name: 'Transportation', percentage: 3, amount: 2250, color: 'bg-red-500' },
    { id: 8, name: 'Miscellaneous', percentage: 4, amount: 3000, color: 'bg-gray-500' }
  ]);

  const [newCategory, setNewCategory] = useState({ name: '', percentage: 0 });

  const handleBudgetChange = (newBudget) => {
    setBudget(newBudget);
    // Update all category amounts based on new budget
    setCategories(prev => 
      prev.map(cat => ({
        ...cat,
        amount: (newBudget * cat.percentage) / 100
      }))
    );
  };

  const handleCategoryPercentageChange = (categoryId, newPercentage) => {
    setCategories(prev => 
      prev.map(cat => 
        cat.id === categoryId 
          ? { ...cat, percentage: newPercentage, amount: (budget * newPercentage) / 100 }
          : cat
      )
    );
  };

  const handleAddCategory = () => {
    if (newCategory.name.trim() && newCategory.percentage > 0) {
      const newCat = {
        id: Date.now(),
        name: newCategory.name.trim(),
        percentage: newCategory.percentage,
        amount: (budget * newCategory.percentage) / 100,
        color: 'bg-teal-500'
      };
      setCategories(prev => [...prev, newCat]);
      setNewCategory({ name: '', percentage: 0 });
    }
  };

  const handleDeleteCategory = (categoryId) => {
    setCategories(prev => prev.filter(cat => cat.id !== categoryId));
  };

  const totalPercentage = categories.reduce((sum, cat) => sum + cat.percentage, 0);
  const totalAllocated = categories.reduce((sum, cat) => sum + cat.amount, 0);
  const remaining = budget - totalAllocated;

  return (
    <div className="space-y-6">
      {/* Budget Overview */}
      <div className="bg-white rounded-xl shadow-soft p-6">
        <h2 className="font-heading font-semibold text-2xl text-text-primary mb-6">
          Budget Calculator
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="text-center">
            <div className="bg-primary-50 rounded-lg p-4">
              <Icon name="DollarSign" size={24} className="text-primary mx-auto mb-2" />
              <p className="text-text-secondary text-sm">Total Budget</p>
              <p className="text-2xl font-bold text-primary">
                ${budget.toLocaleString()}
              </p>
            </div>
          </div>
          <div className="text-center">
            <div className="bg-green-50 rounded-lg p-4">
              <Icon name="PieChart" size={24} className="text-green-600 mx-auto mb-2" />
              <p className="text-text-secondary text-sm">Allocated</p>
              <p className="text-2xl font-bold text-green-600">
                ${totalAllocated.toLocaleString()}
              </p>
            </div>
          </div>
          <div className="text-center">
            <div className={`${remaining >= 0 ? 'bg-blue-50' : 'bg-red-50'} rounded-lg p-4`}>
              <Icon name="Wallet" size={24} className={`${remaining >= 0 ? 'text-blue-600' : 'text-red-600'} mx-auto mb-2`} />
              <p className="text-text-secondary text-sm">Remaining</p>
              <p className={`text-2xl font-bold ${remaining >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                ${remaining.toLocaleString()}
              </p>
            </div>
          </div>
        </div>

        {/* Budget Input */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-text-primary mb-2">
            Total Wedding Budget
          </label>
          <div className="relative">
            <span className="absolute left-3 top-3 text-text-secondary">$</span>
            <input
              type="number"
              value={budget}
              onChange={(e) => handleBudgetChange(Number(e.target.value))}
              className="w-full pl-8 pr-4 py-3 border border-border rounded-lg focus:outline-none focus:border-primary transition-colors"
              min="0"
              step="1000"
            />
          </div>
        </div>

        {/* Budget Allocation Warning */}
        {totalPercentage !== 100 && (
          <div className={`mb-6 p-4 rounded-lg border ${
            totalPercentage > 100 ? 'bg-red-50 border-red-200' : 'bg-yellow-50 border-yellow-200'
          }`}>
            <div className="flex items-center space-x-2">
              <Icon name="AlertTriangle" size={20} className={totalPercentage > 100 ? 'text-red-600' : 'text-yellow-600'} />
              <p className={`text-sm font-medium ${totalPercentage > 100 ? 'text-red-800' : 'text-yellow-800'}`}>
                {totalPercentage > 100 
                  ? `Budget over-allocated by ${totalPercentage - 100}%`
                  : `${100 - totalPercentage}% of budget unallocated`
                }
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Budget Categories */}
      <div className="bg-white rounded-xl shadow-soft p-6">
        <h3 className="font-heading font-semibold text-lg text-text-primary mb-4">
          Budget Categories
        </h3>

        <div className="space-y-4 mb-6">
          {categories.map((category) => (
            <div key={category.id} className="border border-border rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-3">
                  <div className={`w-4 h-4 rounded-full ${category.color}`}></div>
                  <span className="font-medium text-text-primary">{category.name}</span>
                </div>
                <button
                  onClick={() => handleDeleteCategory(category.id)}
                  className="text-red-500 hover:text-red-700 transition-colors"
                >
                  <Icon name="Trash2" size={16} />
                </button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-1">
                    Percentage
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      value={category.percentage}
                      onChange={(e) => handleCategoryPercentageChange(category.id, Number(e.target.value))}
                      className="w-full pr-8 pl-4 py-2 border border-border rounded-lg focus:outline-none focus:border-primary transition-colors"
                      min="0"
                      max="100"
                      step="1"
                    />
                    <span className="absolute right-3 top-2 text-text-secondary">%</span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-1">
                    Amount
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-2 text-text-secondary">$</span>
                    <input
                      type="text"
                      value={category.amount.toLocaleString()}
                      readOnly
                      className="w-full pl-8 pr-4 py-2 border border-border rounded-lg bg-surface-100 text-text-secondary"
                    />
                  </div>
                </div>
              </div>
              
              {/* Progress Bar */}
              <div className="mt-3">
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className={`h-2 rounded-full transition-all duration-300 ${category.color}`}
                    style={{ width: `${Math.min(category.percentage, 100)}%` }}
                  ></div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Add New Category */}
        <div className="border-t border-border-light pt-4">
          <h4 className="font-medium text-text-primary mb-3">Add New Category</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <input
              type="text"
              placeholder="Category name"
              value={newCategory.name}
              onChange={(e) => setNewCategory(prev => ({ ...prev, name: e.target.value }))}
              className="px-4 py-2 border border-border rounded-lg focus:outline-none focus:border-primary transition-colors"
            />
            <div className="relative">
              <input
                type="number"
                placeholder="Percentage"
                value={newCategory.percentage || ''}
                onChange={(e) => setNewCategory(prev => ({ ...prev, percentage: Number(e.target.value) }))}
                className="w-full pr-8 pl-4 py-2 border border-border rounded-lg focus:outline-none focus:border-primary transition-colors"
                min="0"
                max="100"
                step="1"
              />
              <span className="absolute right-3 top-2 text-text-secondary">%</span>
            </div>
            <Button
              variant="primary"
              iconName="Plus"
              onClick={handleAddCategory}
              disabled={!newCategory.name.trim() || newCategory.percentage <= 0}
            >
              Add Category
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BudgetCalculator;